import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, X, ChevronRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { GATE_PASS_STATUS_META } from "./lib";

const VIEWS = [
  { key: "",                 label: "All" },
  { key: "issued",           label: "Awaiting Exit" },
  { key: "vehicle_exited",   label: "Vehicle Exited" },
  { key: "cancelled",        label: "Cancelled" },
];

export default function GatePassList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [overrideOnly, setOverrideOnly] = useState(false);
  const [overview, setOverview] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status) params.status = status;
    if (overrideOnly) params.override_only = true;
    api.get("/billing/gate-passes", { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
    api.get("/billing/gate-passes/overview").then((r) => setOverview(r.data.kpis));
  };
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, status, overrideOnly]);

  return (
    <div className="space-y-4" data-testid="gate-pass-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Gate Pass</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            {rows.length} pass{rows.length === 1 ? "" : "es"} · controlled vehicle exit.
          </p>
        </div>
        <Link to="/gate-pass/new" data-testid="gate-pass-new-btn"
              className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
          <Plus size={13} /> New Gate Pass
        </Link>
      </header>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="gate-pass-kpis">
          <MiniKpi label="Awaiting exit" value={overview.awaiting_exit} tone="amber" />
          <MiniKpi label="Exited today" value={overview.issued_today} tone="steel" />
          <MiniKpi label="Vehicle exited" value={overview.vehicle_exited} tone="green" />
          <MiniKpi label="Credit override" value={overview.override_count} tone="red" />
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1">
          {VIEWS.map((v) => (
            <button key={v.key || "all"} onClick={() => setStatus(v.key)}
                    data-testid={`gp-view-${v.key || "all"}`}
                    className={`h-8 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
                      status === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
                    }`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search GP #, invoice, JC, customer, vehicle…"
                 data-testid="gate-pass-search"
                 className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]">
              <X size={14} />
            </button>
          )}
        </div>
        <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1d3557]">
          <input type="checkbox" checked={overrideOnly} onChange={(e) => setOverrideOnly(e.target.checked)}
                 data-testid="gp-override-only"
                 className="w-4 h-4 accent-[#e63946]" />
          Override only
        </label>
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="gate-pass-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <ShieldCheck size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No gate passes yet</div>
            <p className="text-[12px] text-[#667085] mt-1">Issue a gate pass to release a delivered vehicle.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="gate-pass-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5">Number</th>
                  <th className="px-3 py-2.5">Vehicle</th>
                  <th className="px-3 py-2.5">Customer</th>
                  <th className="px-3 py-2.5">Invoice</th>
                  <th className="px-3 py-2.5 text-right">Balance</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Handed To</th>
                  <th className="px-3 py-2.5">Exit</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = GATE_PASS_STATUS_META[r.status] || GATE_PASS_STATUS_META.draft;
                  return (
                    <tr key={r.id} data-testid={`gp-row-${r.id}`}
                        onClick={() => nav(`/gate-pass/${r.id}`)}
                        className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer">
                      <td className="px-5 py-3 font-mono font-semibold text-[#1d3557]">
                        {r.number}
                        {r.override_used && (
                          <span className="ml-1 inline-flex px-1.5 py-0 rounded-full text-[9px] font-bold bg-[#e63946]/12 text-[#e63946]">
                            OVR
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 font-mono text-[11.5px]">{r.vehicle_snapshot?.reg_number || "—"}</td>
                      <td className="px-3 py-3 text-[#1d3557]">{r.customer_snapshot?.name || "—"}</td>
                      <td className="px-3 py-3 font-mono text-[11.5px] text-[#667085]">{r.invoice_number || "—"}</td>
                      <td className={`px-3 py-3 text-right font-mono ${r.balance > 0 ? "text-[#e63946]" : "text-[#0F9256]"}`}>
                        ₹{(Number(r.balance) || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${st.chip}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[#1d3557] text-[11.5px]">{r.handed_to_name || "—"}</td>
                      <td className="px-3 py-3 text-[11px] text-[#667085]">
                        {r.vehicle_exited_at ? new Date(r.vehicle_exited_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-5 py-3 text-right text-[#a8dadc]"><ChevronRight size={14} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function MiniKpi({ label, value, tone }) {
  const map = {
    red:   "border-[#e63946]/30 text-[#e63946]",
    amber: "border-[#F79009]/30 text-[#B54708]",
    green: "border-[#12B76A]/30 text-[#0F9256]",
    steel: "border-[#E4E7EC] text-[#457b9d]",
  };
  return (
    <div className={`rounded-lg bg-white border ${map[tone]} p-3`}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className="text-[22px] font-bold font-mono tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}
